import type { Enums } from "@bednbody/types/database";
import { HubSelect } from "@/components/HubSelect";
import { Button } from "@bednbody/ui/components/button";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  ComboboxValue,
} from "@bednbody/ui/components/combobox";
import { Input } from "@bednbody/ui/components/input";
import { Label } from "@bednbody/ui/components/label";
import { type OpsFilters, useHubsList } from "../ops-dashboard.hooks";

const ALL = "__ALL__";

const STATUS_OPTIONS: (Enums<"booking_status"> | typeof ALL)[] = [
  ALL,
  "pending",
  "awaiting_rider",
  "dispatched",
  "en_route_to_client",
  "arrived",
  "in_session",
  "completed",
  "cancelled",
];

interface BookingFiltersProps {
  filters: OpsFilters;
  onChange: (next: OpsFilters) => void;
  /** Hub Managers don't get a hub picker — RLS already scopes them to one. */
  showHubPicker: boolean;
  showStatus?: boolean;
}

export function BookingFilters({
  filters,
  onChange,
  showHubPicker,
  showStatus = true,
}: BookingFiltersProps) {
  const { data: hubs } = useHubsList();

  return (
    <>
      {showHubPicker ? (
        <Label className="flex items-center gap-1.5 type-body-default text-muted-foreground">
          Hub
          <HubSelect
            ariaLabel="Hub"
            className="w-[150px]"
            hubs={hubs ?? []}
            variant="combobox"
            value={filters.hubId}
            onValueChange={(hubId) => onChange({ ...filters, hubId })}
          />
        </Label>
      ) : null}

      {showStatus ? (
        <Label className="flex items-center gap-1.5 type-body-default text-muted-foreground">
          Status
          <Combobox
            items={STATUS_OPTIONS}
            value={filters.status ?? ALL}
            onValueChange={(value) =>
              onChange({
                ...filters,
                status: value === ALL ? null : (value as Enums<"booking_status">),
              })
            }
            itemToStringLabel={(val) => (val === ALL ? "All statuses" : val.replace(/_/g, " "))}
          >
            <ComboboxTrigger
              render={
                <Button variant="secondary" className="w-[150px] justify-between font-normal" />
              }
            >
              {/* Same as the hub trigger above — fixed-width Button, so the value
                  needs its own shrinkable wrapper to ellipsise. */}
              <span className="min-w-0 truncate">
                <ComboboxValue placeholder="All statuses" />
              </span>
            </ComboboxTrigger>
            <ComboboxContent>
              <ComboboxInput placeholder="Search statuses…" showTrigger={false} />
              <ComboboxEmpty>No statuses found.</ComboboxEmpty>
              <ComboboxList>
                {(status) => (
                  <ComboboxItem key={status} value={status} className="capitalize">
                    {status === ALL ? "All statuses" : status.replace(/_/g, " ")}
                  </ComboboxItem>
                )}
              </ComboboxList>
            </ComboboxContent>
          </Combobox>
        </Label>
      ) : null}

      <Label className="flex items-center gap-1.5 type-body-default text-muted-foreground">
        From
        <Input
          type="date"
          className="w-[150px]"
          value={filters.dateFrom ?? ""}
          onChange={(e) => onChange({ ...filters, dateFrom: e.target.value || null })}
        />
      </Label>

      <Label className="flex items-center gap-1.5 type-body-default text-muted-foreground">
        To
        <Input
          type="date"
          className="w-[150px]"
          value={filters.dateTo ?? ""}
          onChange={(e) => onChange({ ...filters, dateTo: e.target.value || null })}
        />
      </Label>
    </>
  );
}
